import {
  KeyboardArrowDown as ArrowDropDownIcon,
  Star as StarIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { IconButton, Button, Container, Collapse } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { signOut } from "../../api/AuthAPI.js";
import useBool from "../../hooks/useBool.js";
import Brand from "../adhara/brand/Brand.js";
import { TrainingsIcon } from "../adhara/icons/AdminIcons.js";
import { CartIcon, HeartIcon } from "../adhara/icons/CommonIcons.js";
import { useAuth } from "../auth/AuthProvider.js";
import ButtonLight from "../buttons/ButtonLight.js";
import ButtonPrimary from "../buttons/ButtonPrimary.js";
import ConfirmedButton from "../buttons/ConfirmedButton.js";
import CircularProgress from "../loading/CircularProgress.js";
import { useShoppingCart } from "../shopping/CartProvider.js";
import HamburgerNavbar from "./HamburgerNavbar.js";

import MainNavbar from "./MainNavbar.js";
import styles from "./TopBar.module.css";

const TopBar = () => {
  const { user } = useAuth();

  const cartSize = useShoppingCart().items.length;

  // Normal navbar
  const [collapsibleContent, setCollapsibleContent] = useState();

  // Hamburger navbar
  const [hamburgerContent, setHamburgerContent] = useState();
  const [isHamburgerOpen, openHamburger, closeHamburger] = useBool(false);
  const toggleHamburger = () =>
    isHamburgerOpen ? closeHamburger() : openHamburger();
  
  return (
    <header
      className={styles.topbarContainer}
      onMouseLeave={() => setCollapsibleContent(null)}
    >
      <div className={styles.topbar}>
        <Brand size="large" className={styles.topbarLogo} />
        <Brand size="small" className={styles.topbarIcon} />

        <MainNavbar openCollapsible={setCollapsibleContent} />

        {/* search with flex grow */}

        <div className={styles.links}>
          {user && (
            <Link className={styles.favLink} to="/profile/items/trainings">
              <HeartIcon className={styles.cartIcon} />
            </Link>
          )}

          <Link className={styles.shoppingCartLink} to="/cart">
            {cartSize > 0 && (
              <div className={styles.shoppingCartSize}>
                {cartSize >= 100 ? "+99" : cartSize}
              </div>
            )}
            <CartIcon className={styles.cartIcon} />
          </Link>

          <Link
            className={styles.trainingsLinkMobile}
            to="/profile/items/trainings"
          >
            <TrainingsIcon className={styles.cartIcon} />
          </Link>
        </div>
        <HamburgerNavbar
          isOpen={isHamburgerOpen}
          toggle={toggleHamburger}
          setHamburgerContent={setHamburgerContent}
        />

        <div className={styles.authButtons}>
          <AuthButtonLeft user={user} />
          <AuthButtonRight user={user} />
        </div>
      </div>

      <Collapse in={true}>
        <Container style={{ padding: 0 }}>{collapsibleContent || ""}</Container>
      </Collapse>

      <Collapse in={isHamburgerOpen}>{hamburgerContent}</Collapse>
    </header>
  );
};

const AuthButtonLeft = ({ user }) => {
  const { t } = useTranslation()

  if (user === null)
    // no user
    return <ButtonLight to="/login">{t("buttons.login")}</ButtonLight>;
};

const AuthButtonRight = ({ user }) => {
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation()

  const logout = () =>
    signOut()
      .then(() => (window.location.href = "/"))
      .catch(console.error);

  const handleToggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  if (user === null)
    // no user
    return <ButtonPrimary to="/register">{t("buttons.register")}</ButtonPrimary>;

  if (user || !user) {
    return (
      <>
        <ButtonLight
          className={styles.trainingsLinkDesktop}
          style={{ borderRadius: "20px" }}
          to="/profile/items/trainings"
        >
          {t("header.my_courses")}
        </ButtonLight>
        <div
          className={styles.settingsMenu}
          onMouseLeave={() => setIsMenuOpen(false)}
        >
          <IconButton
            className={styles.iconButton}
            tabIndex={0}
            role="button"
            aria-controls="settings-menu"
            aria-haspopup="true"
            onClick={handleToggleMenu}
            onMouseEnter={() => setIsMenuOpen(true)}
          >
            {user && (
              <>
                {user?.roles?.includes("influencer") && (
                  <div className={styles.partnerIconContainer}>
                    <StarIcon className={styles.partnerIcon} />
                  </div>
                )}
                <PersonIcon className={styles.icon} />
                <div className={styles.dropdownIconContainer}>
                  <ArrowDropDownIcon className={styles.dropdownIcon} />
                </div>
              </>
            )}

            {!user && (
              <>
                <CircularProgress
                  color="white"
                  size="small"
                  style={{ margin: "0", padding: 0 }}
                />
              </>
            )}
          </IconButton>
          {isMenuOpen && user && (
            <div className={styles.menuContainer}>
              <div id="menu" className={styles.menu}>
                {user?.roles?.includes("influencer") && (
                  <Button
                    type={"button"}
                    onClick={() => navigate("/partner/details")}
                    className={styles.menuItem}
                  >
                    <div className={styles.label}>Parceria</div>
                  </Button>
                )}

                <div className={styles.menuItemBottomDivision} />

                <Button
                  type={"button"}
                  onClick={() => navigate("/profile/details")}
                  className={styles.menuItem}
                >
                  <div className={styles.label}>{t("header.edit_profile")}</div>
                </Button>

                <div className={styles.menuItemBottomDivision} />

                <ConfirmedButton
                  className={styles.menuItem}
                  confirmText={t("modals.leave_session")}
                  confirmTitle={t("common.leave")}
                  onClick={logout}
                >
                  <div className={styles.label}>{t("common.leave")}</div>
                </ConfirmedButton>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
};

export default TopBar;
